import { useEffect, useState } from 'react';
import { FacingMode, media, MediaEventType } from '../../webrtc/media';

/**
 * Compute whether camera flipping is possible based on available devices.
 */
const getCanFlipCamera = () => {
  const cameras = media.cameras;
  // Must have at least two camera devices to allow flipping.
  if (cameras.length <= 1) {
    return false;
  }

  const modes = new Set<FacingMode>();

  for (const camera of cameras) {
    const facingModes = camera.getCapabilities?.()?.facingMode ?? [];
    if (facingModes.length !== 1) {
      continue;
    }

    const mode = facingModes[0];
    if (mode === FacingMode.User || mode === FacingMode.Environment) {
      modes.add(mode);
    }
  }

  return modes.size > 1;
};

/**
 * Track whether camera flipping is currently possible.
 */
export const useCanFlipCamera = () => {
  const [canFlipCamera, setCanFlipCamera] = useState(getCanFlipCamera());

  useEffect(() => {
    const updateCanFlipCamera = () => {
      setCanFlipCamera(getCanFlipCamera());
    };

    // Initialize and keep in sync with device/permission changes.
    updateCanFlipCamera();

    const unsubscribe = media.on(
      [MediaEventType.MediaAccessStatusChange, MediaEventType.DevicesChange],
      updateCanFlipCamera,
    );

    return () => {
      unsubscribe();
    };
  }, []);

  return canFlipCamera;
};
